import React, { useState, useEffect } from 'react';
import { Layout, Menu, message, Avatar, Dropdown, Typography, Button, Spin } from 'antd';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from '../UI/NotificationBell';
import Sidebar from './Sidebar';

const { Header, Content } = Layout;

const drawerWidth = 260;
const collapsedWidth = 72;

const MainLayout = ({ children }) => {
  const { loading, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  useEffect(() => {
    message.config({
      top: 80,
      duration: 6,
      getContainer: () => document.getElementById('message-root') || document.body,
    });
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 50 }}>
    <Spin size="large" />
  </div>;

  const handleLogout = () => {
    // Must go through AuthContext: removing only the token left `user` behind in
    // localStorage, so the reloaded /login page still believed someone was signed
    // in, fired its authenticated bootstrap calls (push register, active call,
    // lead statuses), got 401s, and the 401 handler bounced it back to /login —
    // reloading forever.
    logout();
    window.location.href = '/login';
  };

  const handleSidebarToggle = () => {
    setCollapsed((prev) => !prev);
  };

  const handleProfile = () => {
    navigate('/profile');
  };

  const profileMenu = (
    <Menu>
      <Menu.Item key="profile" icon={<ProfileOutlined />} onClick={handleProfile}>
        Profile
      </Menu.Item>
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  // Responsive layout: adjust marginLeft for desktop, none for mobile
  const layoutStyle = isMobile
    ? { minHeight: '100vh', marginLeft: 0, transition: 'none' }
    : { minHeight: '100vh', marginLeft: collapsed ? collapsedWidth : drawerWidth, transition: 'margin-left 0.3s' };

  // Responsive header: stack items on mobile, row on desktop
  const headerStyle = {
    padding: isMobile ? '0 12px' : '0 24px',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#001529',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    height: isMobile ? 'auto' : 64,
    minHeight: 56,
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} onToggle={handleSidebarToggle} />
      <Layout style={layoutStyle}>
        <Header style={headerStyle}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 8 : 12,
            width: '100%',
            justifyContent: isMobile ? 'space-between' : 'flex-start',
            paddingTop: isMobile ? 8 : 0,
            paddingBottom: isMobile ? 8 : 0,
          }}>
            
            <Typography.Title
              level={5}
              style={{
                color: '#fff',
                margin: 0,
                fontSize: isMobile ? 16 : 20,
                flex: 1,
                textAlign: isMobile ? 'center' : 'left',
              }}
            >
            </Typography.Title>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <NotificationBell />
              <Dropdown overlay={profileMenu} placement="bottomRight" arrow>
                <Avatar style={{ cursor: 'pointer', marginLeft: isMobile ? 0 : 12 }} icon={<UserOutlined />} />
              </Dropdown>
            </div>
          </div>
        </Header>
        <Content style={{
          margin: isMobile ? '12px' : '24px',
          background: '#fff',
          padding: isMobile ? 12 : 24,
          minHeight: 200,
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
